import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { ClickEvent, DataSourceMode, Image, Layout, SaveButton, SlideoutDecorator, Snackbar, Table, TableColumn, TableRow, TableRowDisplayEvent, Toast } from "@mcleod/components";
import { Api, HorizontalAlignment, Model, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutGeographicCityList } from "./autogen/AutogenLayoutGeographicCityList";
import { RmSpCities } from "./RmSpCities";

export class GeographicCityList extends AutogenLayoutGeographicCityList {

    private layoutRmSpCities = Layout.getLayout("lme/dispatch/RmSpCities") as RmSpCities;
    private intellirouteImage: Image;

    override async onLoad() {
        const tableCity: Table = this.tableCity;
        const column: TableColumn = tableCity.getColumnByCaption("irid") as TableColumn;
        const cityCodeCol = tableCity.getColumnByCaption("City Code") as TableColumn;
        if (cityCodeCol && cityCodeCol.headingCell) {
            cityCodeCol.headingCell.align = HorizontalAlignment.LEFT;
        }
        const response = await Api.search("lme/dispatch/validate-intelliroute-row");
        const isRowExist: boolean = response?.data[0]?.row_exist ?? false;
        if (!isRowExist) {
            if (column) {
                tableCity.setColumnVisibility(column, false);
                return;
            }
        }
        this.setupTableCity(tableCity);
        if (column) {
            column.headingCell.caption = null;
            tableCity.setColumnVisibility(column, true);
        }
    }

    tableCityOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.target as TableRow;
        const modelRow: ModelRow = row.data as ModelRow;
        const intellirouteCityId = modelRow.get("intelliroute_id");
        if (intellirouteCityId != null) {
            const intellirouteImageIcon = row.findComponentById("imageIntelliroute") as Image;
            if (intellirouteImageIcon) {
                intellirouteImageIcon.visible = true;
                CommonTooltips.setTooltipContentLayout(intellirouteImageIcon, intellirouteCityId, "lme/dispatch/IntellirouteCityQuickInfo");
            }
        }
    }

    setupTableCity(tableCity: Table) {
        tableCity.addSelectionListener(() => this.updateImageTooltipAndState(this.tableCity));
        this.createGeographicCityImages();
        this.tableCity.addCenterTool(this.intellirouteImage);
    }

    createGeographicCityImages() {
        this.intellirouteImage = new Image({
            name: ImageName.intelliroute,
            fontSize: '16',
            color: 'default.lightest',
            tooltip: "Add Intelliroute City for selected row",
            enabled: false,
            marginRight: '16',
            onClick: () => this.openIntellirouteSlideout()
        });
    }

    updateImageTooltipAndState(tableCity: Table) {
        const numberOfRowsSelected = tableCity.selectedRowCount;
        this.intellirouteImage.enabled = numberOfRowsSelected == 1 ? true : false;
        this.intellirouteImage.color = numberOfRowsSelected == 1 ? 'primary' : 'default.lightest';
        if (numberOfRowsSelected === 0) {
            this.intellirouteImage.disabledTooltip = "You must select a row to add Intelliroute City.";
            return;
        }
        const selectedRow: TableRow = tableCity.selectedRow;
        const interllirouteId = selectedRow.data.get("intelliroute_id", null);
        if (!StringUtil.isEmptyString(interllirouteId)) {
            this.intellirouteImage.disabledTooltip = "Intelliroute City already exists for City ID: " + interllirouteId;
            this.intellirouteImage.enabled = false;
            this.intellirouteImage.color = 'default.lightest';
        }
    }

    private intellirouteAddButton: SaveButton = SaveButton.create({
        id: "intellirouteAddButton",
        caption: "Add",
        rowBreak: false,
        enabled: false,
        dataSource: this.layoutRmSpCities.sourceRmSpcities,
        onClick: (event: ClickEvent) => {
            const rmspCityRow: ModelRow = this.layoutRmSpCities.tableRmspCities.selectedRow.data as ModelRow;
            const cityId: number = this.tableCity.selectedRow.data.get("id", null);
            const inputs = {
                city_id: cityId as number,
                name: rmspCityRow.get("name", null) as string,
                county: rmspCityRow.get("county", null) as string,
                state_id: rmspCityRow.get("state_id", null) as string,
                zip_code: rmspCityRow.get("zip_code", null) as string,
                latitude: rmspCityRow.get("latitude", null) as number,
                longitude: rmspCityRow.get("longitude", null) as number
            }
            Api.post("lme/dispatch/create-intelliroute-city", inputs).then(async output => {
                const errorMessage = output?.data[0].error_message;
                if (!errorMessage) {
                    const selectedRow = this.tableCity.selectedRow as TableRow;
                    const newCityRow = await Model.searchSingleRecord("lme/dispatch/geographic-city", { id: cityId });
                    this.tableCity.dataSource.replaceRow(selectedRow.index, newCityRow);
                    Toast.showSuccessToast("Intelliroute City added successfully");
                    await this.delay(5000);
                    this.updateImageTooltipAndState(this.tableCity);
                    this.layoutRmSpCities.slideOut();
                } else {
                    Snackbar.showWarningSnackbar(`Error adding Intelliroute city: ${errorMessage}`);
                }
            });
        }
    });

    private openIntellirouteSlideout() {
        const selectedRow = this.tableCity.selectedRow as TableRow;
        if (selectedRow == null) {
            return;
        }
        const modelRow = selectedRow.data as ModelRow
        const stateId: string = modelRow.get("state_id", null);
        const latitude: number = modelRow.get("latitude", null);
        const longitude: number = modelRow.get("longitude", null);

        new SlideoutDecorator({
            title: "Add Intelliroute City",
            layout: this.layoutRmSpCities,
            fillVerticalSpace: true,
            width: 1000,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.intellirouteAddButton,
            onClose: async (cancelled: boolean) => {
                const selectedRmSpCityRow: TableRow = this.layoutRmSpCities.tableRmspCities.selectedRow;
                if (selectedRmSpCityRow == null) {
                    this.layoutRmSpCities.slideOut();
                    return;
                }
                const message = "You have a row selected. Do you want to leave without saving?";
                const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                const leaveStaySelection = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                if (leaveStaySelection == true) {
                    this.layoutRmSpCities.slideOut();
                }
            },
            layoutLoadListeners: () => {
                this.layoutRmSpCities.sourceRmSpcities.mode = DataSourceMode.NONE;
                this.layoutRmSpCities.tableRmspCities.addSelectionListener(() => {
                    const selectedRmSpCityRow: TableRow = this.layoutRmSpCities.tableRmspCities.selectedRow;
                    this.intellirouteAddButton.enabled = selectedRmSpCityRow != null;
                });
                this.layoutRmSpCities.sourceRmSpcities.search({ state_id: stateId, latitude: latitude, longitude: longitude });
            }
        });
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

}
